import type {
  CustomerStatusBase,
  CustomerStatusSummary,
} from 'services/customerStatusService';
import type { BaseTerritoryData } from 'services/basesServices';
import type { BaseFlags } from 'services/baseTerritoryService';

/**
 * Mock de desenvolvimento do bloqueio de abastecimento — STRY0050468.
 *
 * A rota `GET /customers/status-summary` só existe a partir da R23 do
 * Sensedia. Enquanto ela não sobe, este mock permite ver as telas reais
 * (home bloqueada, `/pendencias` e o passo 1 do pedido) no navegador.
 *
 * **Só funciona fora de produção** (`NODE_ENV !== 'production'`), e só quando
 * a chave existe no `localStorage` — em uso normal, nada aqui é executado.
 *
 * No console do navegador:
 *
 * ```js
 * localStorage.setItem('@granel:mockBlocking', 'all');   // recarregue a página
 * localStorage.removeItem('@granel:mockBlocking');       // volta ao normal
 * ```
 *
 * Cenários: `none`, `overdue`, `payment-term`, `cnpj`, `bases`, `all`,
 * `api-down`, `blocks-missing`.
 */

export const MOCK_KEY = '@granel:mockBlocking';

export type BlockingMockScenario =
  | 'none'
  | 'overdue'
  | 'payment-term'
  | 'cnpj'
  | 'bases'
  | 'all'
  | 'api-down'
  | 'blocks-missing';

const territory = (irregular: boolean): BaseTerritoryData =>
  ({
    name: 'ROTA - SUZANO - CENTRO (QUI)',
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: true,
    friday: false,
    saturday: false,
    sunday: false,
    useNewRefuelRule: true,
    base: {
      suplayWay: 'Pedido',
      suplayDays: 'Quinta-Feira',
      suplayFrequency: 'PEDIDO',
      numberOfDaysToSuplay: 1,
      active: !irregular,
      gasReleased: true,
      oracleStatus: irregular ? 'Inativa' : 'Ativa',
    },
  } as BaseTerritoryData);

/** Bases no formato do contrato novo: território embutido no resumo. */
const mockBases = (firstIrregular: boolean): CustomerStatusBase[] => [
  {
    addressCode: '8634165',
    name: '2501267280.01',
    nickname: 'A3',
    status: 'A',
    territory: territory(firstIrregular),
    resumeStatus: true,
  },
  {
    addressCode: '8634165',
    name: '160727977.01',
    nickname: 'Base Unica',
    status: 'A',
    territory: territory(false),
    resumeStatus: true,
  },
];

const REGULAR: CustomerStatusSummary = {
  fiscal: { status: 'ATIVA', regular: true },
  registration: {
    stateRegistration: {
      serasa: ['000025627.79-90'],
      salesforce: '0000256277990',
      match: true,
    },
  },
  paymentTerm: '28 DDL',
  financial: { hasOverdueBankSlips: false, overdueCount: 0 },
  bases: mockBases(false),
};

const SUMMARY_BY_SCENARIO: Record<
  BlockingMockScenario,
  CustomerStatusSummary | null
> = {
  none: REGULAR,
  overdue: {
    ...REGULAR,
    financial: { hasOverdueBankSlips: true, overdueCount: 3 },
  },
  // Texto real do Salesforce, como veio do sandbox.
  'payment-term': {
    ...REGULAR,
    paymentTerm: '0  -  Condições de Pagamento A Vista',
  },
  cnpj: { ...REGULAR, fiscal: { status: 'SUSPENSA', regular: false } },
  bases: { ...REGULAR, bases: mockBases(true) },
  all: {
    fiscal: { status: 'INAPTA', regular: false },
    registration: { stateRegistration: { match: false } },
    paymentTerm: '0  -  Condições de Pagamento A Vista',
    financial: { hasOverdueBankSlips: true, overdueCount: 2 },
    bases: mockBases(true),
  },
  /** A chamada inteira falha (RN12). */
  'api-down': null,
  /** 200 sem nenhum bloco: nada verificado. */
  'blocks-missing': {},
};

/** Cenários em que as bases voltam irregulares. */
const IRREGULAR_BASES_SCENARIOS: BlockingMockScenario[] = ['bases', 'all'];

const isEnabled = (): boolean =>
  process.env.NODE_ENV !== 'production' && typeof window !== 'undefined';

export const getBlockingMockScenario = (): BlockingMockScenario | null => {
  if (!isEnabled()) return null;

  try {
    const value = localStorage.getItem(MOCK_KEY) as BlockingMockScenario | null;
    return value && value in SUMMARY_BY_SCENARIO ? value : null;
  } catch (error) {
    // localStorage bloqueado (aba anônima, por exemplo): sem mock.
    return null;
  }
};

/**
 * Resumo de situação simulado.
 * `undefined` = sem mock ativo (segue para a API);
 * `null` = simular falha da chamada.
 */
export const getMockCustomerStatusSummary = ():
  | CustomerStatusSummary
  | null
  | undefined => {
  const scenario = getBlockingMockScenario();
  if (!scenario) return undefined;

  return SUMMARY_BY_SCENARIO[scenario];
};

/**
 * Flags simuladas de uma base. `undefined` = sem mock ativo.
 * A primeira base do cliente fica irregular nos cenários `bases` e `all`.
 */
export const getMockBaseFlags = (
  baseCode: string,
  allBaseCodes: string[]
): BaseFlags | undefined => {
  const scenario = getBlockingMockScenario();
  if (!scenario) return undefined;

  const shouldBeIrregular =
    IRREGULAR_BASES_SCENARIOS.includes(scenario) &&
    baseCode === allBaseCodes[0];

  return shouldBeIrregular
    ? { active: false, gasReleased: true, oracleStatus: 'Inativa' }
    : { active: true, gasReleased: true, oracleStatus: 'Ativa' };
};
